/* eslint no-param-reassign: 0 */
import { EventConsts } from 'client/util/Constants';
import { cloneDeep, isEmpty } from 'lodash';
import ModelMixin from 'client/store/modules/models/mixins/ModelMixin';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';
import PageNavigationUtil from 'client/util/PageNavigationUtil';

export const getDefaultItem = () => ({
  _id: '',
  name: '',
});

/**
 * The OrganizationModel Vuex module provides an interface to the Organization
 * item being created or updated in the Org List view.
 */
export default {
  namespaced: true,

  state: {
    item: getDefaultItem(),

    // Explicitly determine whether an item is being created or updated. This
    // is useful so that the UI can determine the _precise_ state of an item.
    itemState: {
      create: undefined,
      update: undefined,
    },

    previousItem: getDefaultItem(),
  },

  actions: {
    ...ModelMixin.actions,

    fetchItem(context, orgId) {
      const { id } = context.getters;
      if (id || orgId) {
        return OrganizationsApi
          .get(id || orgId)
          .then((item) => {
            // Clone the item so that any changes to non-primitive type values,
            // such as an array, are _not_ accidentally made to the previous
            // item when the item is mutated.
            context.commit('SET_ITEM', cloneDeep(item));
            context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
          });
      }

      return Promise.resolve();
    },

    saveItem(context) {
      const { getters } = context;

      const handleSaveItem = (item) => {
        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });

        // Ensure that the created or updated item is updated in the Org List
        // View by passing the item to the OrganizationsCollection so it can be
        // synced in the store immediately.
        context.dispatch('OrganizationsCollection/refreshCollection', { item }, { root: true });

        // Trigger an org event so that all open tabs can refresh the orgs list
        // and then remove so it doesn't pollute localStorage
        PageNavigationUtil.setLocalStorageItem(EventConsts.orgEvent, true);
        PageNavigationUtil.removeLocalStorageItem(EventConsts.orgEvent);
        return item;
      };

      return getters.isItemBeingCreated
        ? OrganizationsApi.create(getters.item).then(handleSaveItem)
        : OrganizationsApi.update(getters.item).then(handleSaveItem);
    },

    updateTrustedAppsSettings(context, trustedApps) {
      const { getters } = context;
      const { item } = getters;
      const updatedSettings = isEmpty(trustedApps)
        ? { ...item.settings, trustedAppConfig: {} }
        : { ...item.settings, trustedAppConfig: { trustedApps } };

      context.dispatch('setItemField', { key: 'settings', value: updatedSettings });
    },

    async subscribeToPlan(_, {
      components,
      isAnnualContract,
      orgId,
      productCode,
      userCount,
    }) {
      await OrganizationsApi.addSubscription({
        committedUsers: userCount,
        components,
        contractType: isAnnualContract ? 'Annual' : 'Monthly',
        orgId,
        productCode,
      });
    },
  },

  getters: {
    ...ModelMixin.getters,

    /**
     * Currently the only required field is the organization's name
     */
    areRequiredFieldsSet(state) {
      return !!state.item.name;
    },

    displayName(state) {
      return state.item.name || '';
    },

    /**
     * Legacy items that reside in Mongo use the _id field while newer items
     * that live in Postgres, such as Policies and Groups, use the id field.
     */
    idField() {
      return '_id';
    },
  },

  mutations: {
    ...ModelMixin.mutations,

    RESET_ITEM(state) {
      state.item = getDefaultItem();
      state.itemState.create = undefined;
      state.itemState.update = undefined;
      state.previousItem = getDefaultItem();
    },
  },
};
